import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import axiosInstance from '../../../api/axiosInstance';

// ─── Toast Notification (Premium Top-Right) ───────────────────────────────────
const Toast = ({ show, type, title, message, onClose }) => {
  useEffect(() => {
    if (show) { const t = setTimeout(onClose, 4000); return () => clearTimeout(t); }
  }, [show, onClose]);
  
  if (!show) return null;
  const col = type === 'success' ? 'bg-green-500' : 'bg-red-500';
  
  return createPortal(
    <div className="fixed top-24 right-6 z-[100000] animate-in slide-in-from-top-4 slide-in-from-right-8 fade-in duration-300">
      <style>{`@keyframes sw{from{width:100%}to{width:0%}}.sw{animation:sw 4s linear forwards}`}</style>
      <div className="flex items-center gap-3 p-4 relative rounded-xl w-[380px] shadow-2xl bg-white dark:bg-slate-800 overflow-hidden border border-slate-200 dark:border-slate-700" role="alert">
        <span className={`absolute w-1 rounded-full h-[70%] left-2 top-1/2 -translate-y-1/2 ${col}`} />
        
        {/* Progress Bar (Shrinks right-to-left) */}
        <span className={`absolute h-1 bottom-0 right-0 sw ${col}`} />
        
        <div className={`ml-2 flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${type === 'success' ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'}`}>
          {type === 'success'
            ? <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"/></svg>
            : <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"/></svg>
          }
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-base font-bold text-slate-900 dark:text-white">{title}</p>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{message}</p>
        </div>
        <button onClick={onClose} className="p-1.5 flex-shrink-0 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-md transition-colors">
          <svg className="w-4 h-4 fill-slate-400 hover:fill-slate-700 dark:hover:fill-slate-200" viewBox="0 0 320.591 320.591">
            <path d="M30.391 318.583a30.37 30.37 0 0 1-21.56-7.288c-11.774-11.844-11.774-30.973 0-42.817L266.643 10.665c12.246-11.459 31.462-10.822 42.921 1.424 10.362 11.074 10.966 28.095 1.414 39.875L51.647 311.295a30.366 30.366 0 0 1-21.256 7.288z"/>
            <path d="M287.9 318.583a30.37 30.37 0 0 1-21.257-8.806L8.83 51.963C-2.078 39.225-.595 20.055 12.143 9.146c11.369-9.736 28.136-9.736 39.504 0l259.331 257.813c12.243 11.462 12.876 30.679 1.414 42.922-.456.487-.927.958-1.414 1.414a30.368 30.368 0 0 1-23.078 7.288z"/>
          </svg>
        </button>
      </div>
    </div>,
    document.body
  );
};

// ─── Compact Download Toast (Light & Dark Theme) ──────────────────────────────
const DownloadToast = ({ show, filename = "Export.csv", onClose }) => {
  useEffect(() => {
    if (show) { const t = setTimeout(onClose, 3000); return () => clearTimeout(t); }
  }, [show, onClose]);

  if (!show) return null;

  return createPortal(
    <div className="fixed bottom-6 right-6 z-[100000] animate-in slide-in-from-bottom-4 slide-in-from-right-8 fade-in duration-300">
      <style>{`@keyframes shrink{from{width:100%}to{width:0%}}.shrink{animation:shrink 3s linear forwards}`}</style>
      
      {/* Container: White in light mode, Slate-800 in dark mode */}
      <div className="flex items-center gap-3 py-2.5 px-3.5 relative rounded-lg shadow-xl dark:shadow-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 min-w-[240px] max-w-[320px] overflow-hidden transition-colors">
        
        {/* Progress Line */}
        <span className="absolute h-[2px] bottom-0 right-0 shrink bg-emerald-500 dark:bg-emerald-400" />
        
        {/* Success Icon */}
        <div className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"/></svg>
        </div>
        
        {/* Text Details */}
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-bold truncate tracking-wide">
            <span className="text-emerald-600 dark:text-emerald-400 mr-1.5 uppercase">Downloaded:</span>
            <span className="text-slate-700 dark:text-slate-200 font-medium">{filename}</span>
          </p>
        </div>
        
        {/* Close Button */}
        <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700 dark:hover:text-white transition-colors rounded">
          <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
        </button>
      </div>
    </div>,
    document.body
  );
};
// ─── Badge ────────────────────────────────────────────────────────────────────
const Badge = ({ status }) => {
  const s = (status || '').toLowerCase();
  const cls = {
    approved: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    pending:  'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
    rejected: 'bg-red-100   text-red-800   dark:bg-red-900/30   dark:text-red-400',
  }[s] || 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300';
  return <span className={`px-2.5 py-1 rounded text-xs font-bold uppercase tracking-wide ${cls}`}>{s || '?'}</span>;
};

// ─── Shared Base Input Class ──────────────────────────────────────────────────
const F = 'py-1.5 px-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md text-sm outline-none focus:border-indigo-500 dark:text-white transition-colors';

// ─── Spinner ──────────────────────────────────────────────────────────────────
const Spin = ({ cls = 'w-5 h-5' }) => (
  <svg className={`animate-spin ${cls}`} fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
  </svg>
);

// ─── Modal wrapper ────────────────────────────────────────────────────────────
const ModalWrap = ({ children, maxWidth = "max-w-sm" }) => createPortal(
  <div className="fixed inset-0 z-[100000] flex items-center justify-center p-4 bg-black/60 dark:bg-black/75 backdrop-blur-sm animate-in fade-in duration-200">
    <div className={`bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 animate-in zoom-in-95 duration-200 w-full ${maxWidth}`}>
      {children}
    </div>
  </div>,
  document.body
);

// ══════════════════════════════════════════════════════════════════════════════
const AllKYC = () => {
  // ── server data ──
  const [allKyc, setAllKyc] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const [downloadToast, setDownloadToast] = useState({ show: false, filename: '' });

  // ── server pagination ──
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const PAGE_LIMIT = 100;

  // ── instant client-side filter state ──
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [dateType, setDateType] = useState('submitted_on');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // ── Mobile Modal State ──
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  const hasFilters = search || filterStatus || fromDate || toDate;

  // ── modals ──
  const [viewModal, setViewModal] = useState(null); 
  const [docImages, setDocImages] = useState({ aadhar_front: null, aadhar_back: null, pan_front: null, pan_back: null });
  const [docsLoading, setDocsLoading] = useState(false);
  const [fullscreenImage, setFullscreenImage] = useState(null);
  const [approveModal, setApproveModal] = useState(null); 
  const [rejectModal, setRejectModal] = useState(null);   
  const [rejectRemark, setRejectRemark] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const [toast, setToast] = useState({ show: false, type: 'success', title: '', message: '' });
  const showToast = (type, title, message) => setToast({ show: true, type, title, message });

  // ── Fetch ──
  const fetchKYC = useCallback(async () => {
    setLoading(true); 
    setError(null);
    const startTime = Date.now();
    try {
      const { data: res } = await axiosInstance.get('/admin/kyc/all', {
        params: { page, limit: PAGE_LIMIT },
      });

      // Delay to allow the skeleton to look smooth (e.g. 800ms)
      const elapsedTime = Date.now() - startTime;
      if (elapsedTime < 400) {
        await new Promise(resolve => setTimeout(resolve, 400 - elapsedTime));
      }
      setAllKyc(res.data || []);
      const m = res.metadata || {};
      const tot = m.total_records ?? m.total ?? 0;
      setTotalRecords(tot);
      setTotalPages(m.total_pages || Math.ceil(tot / PAGE_LIMIT) || 1);
    } catch (err) {
      console.error(err);
      setError('Failed to load KYC data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { fetchKYC(); }, [fetchKYC]);

  // ── CLIENT-SIDE INSTANT FILTER ──
  const filtered = useMemo(() => {
    const q    = search.trim().toLowerCase();
    const from = fromDate ? new Date(fromDate) : null;
    const to   = toDate   ? new Date(toDate + 'T23:59:59') : null;

    return allKyc.filter(k => {
      if (q) {
        const hay = [k.name, k.id, k.user_id, k.aadhar, k.pan].map(v => String(v || '').toLowerCase()).join(' ');
        if (!hay.includes(q)) return false;
      }
      if (filterStatus && k.status?.toLowerCase() !== filterStatus) return false;
      if (from || to) {
        const raw = k[dateType]; 
        if (!raw) return false;
        const d = new Date(raw);
        if (from && d < from) return false;
        if (to   && d > to)   return false;
      }
      return true;
    });
  }, [allKyc, search, filterStatus, dateType, fromDate, toDate]);

  const resetFilters = () => {
    setSearch(''); setFilterStatus(''); setFromDate(''); setToDate('');
    setDateType('submitted_on'); setPage(1);
  };

  // ── View Docs Logic ──
  const openViewModal = async (record) => {
    setViewModal(record);
    setDocsLoading(true);
    
    Object.values(docImages).forEach(url => { if (url) URL.revokeObjectURL(url); });
    
    const types = ['aadhar_front', 'aadhar_back', 'pan_front', 'pan_back'];
    const fetchedImages = { aadhar_front: null, aadhar_back: null, pan_front: null, pan_back: null };

    await Promise.all(types.map(async (type) => {
      try {
        const res = await axiosInstance.get(`/admin/kyc/proof?id=${record.id}&type=${type}`, { responseType: 'blob' });
        fetchedImages[type] = URL.createObjectURL(res.data);
      } catch (err) {
        fetchedImages[type] = null;
      }
    }));

    setDocImages(fetchedImages);
    setDocsLoading(false);
  };

  const closeViewModal = () => {
    setViewModal(null);
    setFullscreenImage(null);
  };

  // ── Approve & Reject Logic ──
  const handleApprove = async () => {
    if (!approveModal) return;
    setActionLoading(true);
    try {
      await axiosInstance.post('/admin/kyc/approve', { kyc_id: approveModal.id });
      showToast('success', 'KYC Approved', `Documents verified for ${approveModal.name || approveModal.id}.`);
      setApproveModal(null);
      fetchKYC();
    } catch {
      showToast('error', 'Approval Failed', 'An error occurred. Please try again.');
    } finally { setActionLoading(false); }
  };

  const handleReject = async () => {
    if (!rejectModal) return;
    setActionLoading(true);
    try {
      await axiosInstance.post('/admin/kyc/reject', { kyc_id: rejectModal.id, remarks: rejectRemark });
      showToast('success', 'KYC Rejected', `Documents rejected for ${rejectModal.name || rejectModal.id}.`);
      setRejectModal(null); setRejectRemark('');
      fetchKYC();
    } catch {
      showToast('error', 'Rejection Failed', 'An error occurred. Please try again.');
    } finally { setActionLoading(false); }
  };

  // ── CSV export ──
  const exportCSV = () => {
    if (!filtered || filtered.length === 0) return;

    // 1. Generate CSV Data
    const headers = ['KYC ID', 'User ID', 'Name', 'Aadhaar', 'PAN', 'Status', 'Submitted', 'Verified'];
    const rows = filtered.map(k => [
      `"${k.id}"`, `"${k.user_id || ''}"`, `"${k.name || ''}"`, `"${k.aadhar || ''}"`, `"${k.pan || ''}"`, 
      `"${k.status || ''}"`, `"${k.submitted_on || ''}"`, `"${k.updated_at || ''}"`
    ].join(','));
    
    // 2. Create the Blob & URL
    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    
    // 3. Create the Link Element safely
    const a = document.createElement('a');
    a.style.display = 'none'; // Keep it hidden
    a.href = url;
    
    // Assign filename to a safe variable BEFORE cleanup
    const safeFilename = `KYC_Export_${new Date().toISOString().slice(0, 10)}.csv`;
    a.download = safeFilename;
    
    // ⚠️ CRITICAL FOR MOBILE SAFARI / CHROME ON ANDROID ⚠️
    document.body.appendChild(a); 
    a.click(); 
    
    // Clean up memory and trigger the toast
    setTimeout(() => {
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      setDownloadToast({ show: true, filename: safeFilename });
    }, 100);
  };

  const fmt = (d) => d
    ? new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(d))
    : '—';

    return (
      <div className="animate-in fade-in duration-200 text-slate-900 dark:text-white flex flex-col h-full relative">
  
        <style>{`
          .ap-bar::-webkit-scrollbar{height:4px;width:4px}
          .ap-bar::-webkit-scrollbar-track{background:transparent}
          .ap-bar::-webkit-scrollbar-thumb{background:#cbd5e1;border-radius:9999px}
          .dark .ap-bar::-webkit-scrollbar-thumb{background:#475569}
          @keyframes blowUpModal { 0% { transform: scale(0); } 100% { transform: scale(1); } }
          @keyframes fadeInBg { 0% { background-color: rgba(17,24,39,0); backdrop-filter: blur(0px); } 100% { background-color: rgba(17,24,39,0.7); backdrop-filter: blur(4px); } }
          .bg-fade-in { animation: fadeInBg 0.3s cubic-bezier(0.165,0.840,0.440,1.000) forwards; }
          .modal-blow-up { animation: blowUpModal 0.3s cubic-bezier(0.165,0.840,0.440,1.000) forwards; }
          
          /* Professional Wavy Shimmer Skeleton */
          @keyframes shimmerWave {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
          }
          .skeleton-box {
            position: relative;
            overflow: hidden;
            background-color: #e2e8f0; /* slate-200 */
            border-radius: 0.375rem;
          }
          .dark .skeleton-box {
            background-color: #334155; /* slate-700 */
          }
          .skeleton-box::after {
            content: '';
            position: absolute;
            top: 0; left: 0; right: 0; bottom: 0;
            background: linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent);
            animation: shimmerWave 1.5s infinite linear;
          }
          .dark .skeleton-box::after {
            background: linear-gradient(90deg, transparent, rgba(255,255,255,0.06), transparent);
          }
        `}</style>
  
        <Toast {...toast} onClose={() => setToast(t => ({ ...t, show: false }))} />

          {/* Download Strip Toast */}
      <DownloadToast 
        show={downloadToast.show} 
        filename={downloadToast.filename} 
        onClose={() => setDownloadToast({ show: false, filename: '' })} 
      />
  
        {/* ── BREADCRUMB ── */}
        <nav className="mb-3 flex-shrink-0">
          <ol className="flex items-center gap-1.5 whitespace-nowrap text-sm">
            <li><Link to="/dashboard/admin" className="text-slate-500 dark:text-slate-400 hover:text-indigo-600 font-semibold">Admin</Link></li>
            <li><svg className="w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeWidth="2.5" d="m9 18 6-6-6-6"/></svg></li>
            <li className="font-semibold text-slate-800 dark:text-slate-200">All KYC</li>
          </ol>
        </nav>
  
        {/* ── HEADER ── */}
        <div className="mb-4 flex-shrink-0">
          <h2 className="text-2xl font-bold leading-tight">Identity Verification</h2>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            Review and verify Aadhaar & PAN submissions
          </p>
        </div>
  
        {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
            SMART SINGLE-ROW TOOLBAR
        ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
        <div className="bg-white dark:bg-slate-800 p-3 rounded-t-lg border border-slate-200 dark:border-slate-700 border-b-0 flex flex-wrap items-center gap-2 sm:gap-3 shadow-sm flex-shrink-0">
          
          {/* 1. Search Bar */}
          <div className="relative flex-grow sm:flex-grow-0 min-w-[160px] max-w-[280px]">
            <svg className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search Name, ID, PAN..."
              className={`${F} pl-8 w-full`} />
          </div>
  
          {/* 2. Mobile Filter Button (< md) */}
          <button onClick={() => setIsFilterModalOpen(true)} className="md:hidden flex items-center justify-center gap-1.5 px-3 py-1.5 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-md text-sm font-bold text-slate-700 dark:text-slate-200 transition-colors border border-slate-200 dark:border-slate-600">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"/></svg>
            Filters {hasFilters && <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>}
          </button>
  
          {/* 3. Inline Filters (Tablet/Desktop only) */}
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className={`${F} hidden md:block w-32 cursor-pointer`}>
            <option value="">All Status</option><option value="approved">Approved</option><option value="pending">Pending</option><option value="rejected">Rejected</option>
          </select>
          
          <div className="hidden md:flex items-center gap-1 px-2 py-1.5 bg-slate-50 dark:bg-slate-900 rounded-md border border-slate-200 dark:border-slate-700 whitespace-nowrap">
            <select value={dateType} onChange={e => setDateType(e.target.value)} className="bg-transparent text-xs font-semibold text-slate-500 dark:text-slate-400 outline-none cursor-pointer">
              <option value="submitted_on">Sub</option><option value="updated_at">Ver</option>
            </select>
            <span className="text-slate-300 dark:text-slate-600 select-none">|</span>
            <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="bg-transparent text-xs text-slate-600 dark:text-slate-300 outline-none cursor-pointer w-[105px]" />
            <span className="text-[10px] font-bold text-slate-400 select-none">–</span>
            <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="bg-transparent text-xs text-slate-600 dark:text-slate-300 outline-none cursor-pointer w-[105px]" />
          </div>
  
          {hasFilters && (
            <button onClick={resetFilters} className="hidden md:flex items-center gap-1 px-2.5 py-1.5 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-md border border-red-200 dark:border-red-800/50 hover:bg-red-100 text-xs font-bold transition-colors">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"/></svg> Clear
            </button>
          )}
  
          {/* 4. Actions (Pushed to the right) */}
          <div className="flex items-center justify-end gap-2.5 ml-auto w-full md:w-auto mt-2 md:mt-0 border-t border-slate-100 dark:border-slate-700 pt-2.5 md:pt-0 md:border-none">
            <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 whitespace-nowrap mr-1">
              {loading ? '…' : hasFilters ? <><span className="font-bold text-slate-800 dark:text-slate-100">{filtered.length}</span>/{allKyc.length}</> : <span className="font-bold text-slate-800 dark:text-slate-100">{allKyc.length}</span>} shown
            </span>
            <button onClick={fetchKYC} disabled={loading} className="flex items-center justify-center p-1.5 sm:px-2.5 bg-slate-50 dark:bg-slate-900 text-slate-600 dark:text-slate-300 rounded-md border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 text-xs font-bold transition-colors disabled:opacity-50">
              <svg className={`w-3.5 h-3.5 sm:mr-1.5 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 11A8.1 8.1 0 004.5 9M4 5v4h4M4 13a8.1 8.1 0 0015.5 2M20 19v-4h-4"/></svg>
              <span className="hidden sm:inline">Refresh</span>
            </button>
            <button onClick={exportCSV} disabled={loading || !filtered.length} className="flex items-center justify-center p-1.5 sm:px-2.5 bg-green-600 hover:bg-green-700 text-white font-bold text-xs rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              <svg className="w-3.5 h-3.5 sm:mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/></svg>
              <span className="hidden sm:inline">Export</span>
            </button>
          </div>
  
        </div>
  
        {error && (
          <div className="px-4 py-2 bg-red-50 dark:bg-red-900/20 border-x border-red-200 dark:border-red-800/50 text-red-700 dark:text-red-400 text-xs font-bold">{error}</div>
        )}
  
        {/* ── TABLE ── */}
        <div className="bg-white dark:bg-slate-800 rounded-b-lg shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden flex flex-col flex-1 min-h-[60vh]">
          <div className="w-full overflow-x-auto flex-1 ap-bar">
            <table className="w-full text-left border-collapse whitespace-nowrap min-w-max">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-900/60 border-b border-slate-200 dark:border-slate-700">
                  {[
                    ['Sr.',        'text-center w-10'],
                    ['View Docs',  'text-center w-12 border-r border-slate-200 dark:border-slate-700'],
                    ['Actions',    'text-center border-r border-slate-200 dark:border-slate-700'],
                    ['KYC ID',     ''],
                    ['User Info',  ''],
                    ['Aadhaar No.', ''],
                    ['PAN No.',    ''],
                    ['Status',     'text-center'],
                    ['Submitted',  'text-center'],
                    ['Verified',   'text-center'],
                  ].map(([label, cls]) => (
                    <th key={label} className={`px-4 py-2.5 text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wide ${cls}`}>
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
  
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700/40">
                {loading ? (
                  // ─── PROFESSIONAL WAVY SKELETON (10 COLUMNS) ───
                  Array.from({ length: 10 }).map((_, i) => (
                    <tr key={i}>
                      <td className="px-4 py-2"><div className="h-4 w-6 skeleton-box mx-auto"></div></td>
                      <td className="px-4 py-2 border-r border-slate-100 dark:border-slate-700/40"><div className="h-7 w-7 skeleton-box mx-auto rounded-md"></div></td>
                      <td className="px-4 py-2 border-r border-slate-100 dark:border-slate-700/40">
                        <div className="flex justify-center gap-1.5">
                          <div className="h-7 w-7 skeleton-box rounded-md"></div>
                          <div className="h-7 w-7 skeleton-box rounded-md"></div>
                        </div>
                      </td>
                      <td className="px-4 py-2"><div className="h-4 w-20 skeleton-box"></div></td>
                      <td className="px-4 py-2">
                        <div className="h-4 w-32 skeleton-box"></div>
                        <div className="h-3 w-20 skeleton-box mt-1"></div>
                      </td>
                      <td className="px-4 py-2"><div className="h-4 w-28 skeleton-box"></div></td>
                      <td className="px-4 py-2"><div className="h-4 w-24 skeleton-box"></div></td>
                      <td className="px-4 py-2"><div className="h-5 w-16 skeleton-box mx-auto rounded"></div></td>
                      <td className="px-4 py-2"><div className="h-4 w-24 skeleton-box mx-auto"></div></td>
                      <td className="px-4 py-2"><div className="h-4 w-24 skeleton-box mx-auto"></div></td>
                    </tr>
                  ))
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="py-20 text-center">
                      <svg className="w-8 h-8 mx-auto mb-2 text-slate-300 dark:text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                      <p className="text-sm font-medium text-slate-500 dark:text-slate-400">No KYC records match your filters.</p>
                    </td>
                  </tr>
                ) : (
                  filtered.map((record, i) => {
                    const isPending = record.status?.toLowerCase() === 'pending';
                    return (
                      <tr key={record.id} className="hover:bg-slate-50/80 dark:hover:bg-slate-700/30 transition-colors">
  
                        <td className="px-4 py-2 text-xs font-medium text-slate-500 dark:text-slate-400 text-center">{(page - 1) * PAGE_LIMIT + i + 1}</td>
  
                        {/* View Docs (Eye Icon) */}
                        <td className="px-4 py-2 text-center border-r border-slate-100 dark:border-slate-700/40">
                          <button onClick={() => openViewModal(record)} title="View Documents"
                            className="p-1.5 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-md hover:bg-blue-600 hover:text-white border border-blue-100 dark:border-blue-800/50 transition-colors inline-block shadow-sm">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                            </svg>
                          </button>
                        </td>
  
                        {/* Actions */}
                        <td className="px-4 py-2 text-center border-r border-slate-100 dark:border-slate-700/40">
                          <div className="flex items-center justify-center gap-1.5">
                            <button onClick={() => setApproveModal(record)} disabled={!isPending}
                              title={isPending ? 'Approve' : 'Already processed'}
                              className={`p-1.5 rounded-md border transition-colors shadow-sm ${isPending
                                ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 hover:bg-green-600 hover:text-white border-green-100 dark:border-green-800/50'
                                : 'bg-slate-100 dark:bg-slate-800 text-slate-300 dark:text-slate-600 border-slate-200 dark:border-slate-700 cursor-not-allowed'}`}>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                            </button>
                            <button onClick={() => setRejectModal(record)} disabled={!isPending}
                              title={isPending ? 'Reject' : 'Already processed'}
                              className={`p-1.5 rounded-md border transition-colors shadow-sm ${isPending
                                ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-600 hover:text-white border-red-100 dark:border-red-800/50'
                                : 'bg-slate-100 dark:bg-slate-800 text-slate-300 dark:text-slate-600 border-slate-200 dark:border-slate-700 cursor-not-allowed'}`}>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                            </button>
                          </div>
                        </td>
  
                        <td className="px-4 py-2 text-sm font-mono text-slate-600 dark:text-slate-400">{record.id}</td>
                        <td className="px-4 py-2">
                          <p className="text-base font-semibold text-slate-900 dark:text-white">{record.name || 'N/A'}</p>
                          <p className="text-xs text-slate-500 font-mono mt-0.5">{record.user_id}</p>
                        </td>
                        <td className="px-4 py-2 text-sm font-mono text-slate-700 dark:text-slate-300 tracking-wider">{record.aadhar || 'Not Provided'}</td>
                        <td className="px-4 py-2 text-sm font-mono text-slate-700 dark:text-slate-300 uppercase tracking-wider">{record.pan || 'Not Provided'}</td>
                        
                        <td className="px-4 py-2 text-center"><Badge status={record.status} /></td>
                        
                        <td className="px-4 py-2 text-center text-sm text-slate-500 dark:text-slate-400 whitespace-nowrap">{fmt(record.submitted_on)}</td>
                        <td className="px-4 py-2 text-center text-sm text-slate-500 dark:text-slate-400 whitespace-nowrap">{fmt(record.updated_at)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
  
          {/* Pagination */}
          {!loading && filtered.length > 0 && (
            <div className="px-6 py-3 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between bg-slate-50 dark:bg-slate-900/50 flex-shrink-0">
              <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">
                Page <strong className="text-slate-800 dark:text-white">{page}</strong> of <strong className="text-slate-800 dark:text-white">{totalPages}</strong>
              </p>
              <div className="flex gap-2">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  className="px-4 py-1.5 text-sm font-bold text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-md hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-40 transition-colors">
                  Prev
                </button>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                  className="px-4 py-1.5 text-sm font-bold text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-md hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-40 transition-colors">
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
  
        {/* ── MODALS ── */}
  
        {/* MOBILE FILTERS MODAL (Boxy Centered) */}
        {isFilterModalOpen && createPortal(
          <div className="fixed inset-0 z-[100000] bg-black/60 dark:bg-black/75 backdrop-blur-sm flex items-center justify-center p-4 md:hidden bg-fade-in">
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 flex flex-col w-full max-w-[340px] max-h-[85vh] modal-blow-up">
              
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 dark:border-slate-700 flex-shrink-0">
                <h3 className="font-bold text-base text-slate-900 dark:text-white flex items-center gap-1.5">
                  <svg className="w-5 h-5 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"/></svg>
                  Filter KYC Records
                </h3>
                <button onClick={() => setIsFilterModalOpen(false)} className="p-1.5 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:bg-slate-600 rounded-md text-slate-500 dark:text-slate-400 transition-colors">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"/></svg>
                </button>
              </div>
  
              <div className="p-5 flex flex-col gap-4 overflow-y-auto custom-scrollbar flex-1">
                <div>
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1.5 block">Status</label>
                  <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className={`${F} w-full py-2`}>
                    <option value="">All Status</option><option value="approved">Approved</option><option value="pending">Pending</option><option value="rejected">Rejected</option>
                  </select>
                </div>
                
                <div>
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1.5 block">Date Range</label>
                  <div className="flex flex-col gap-2 p-3 bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700">
                    <select value={dateType} onChange={e => setDateType(e.target.value)} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md py-1.5 px-2 text-xs font-semibold outline-none text-slate-700 dark:text-slate-300 w-full">
                      <option value="submitted_on">Submitted Date</option><option value="updated_at">Verified Date</option>
                    </select>
                    <div className="flex items-center justify-between gap-1.5 mt-1">
                      <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md py-1.5 px-2 text-[11px] outline-none text-slate-600 dark:text-slate-300 w-full flex-1" />
                      <span className="text-[10px] font-bold text-slate-400">TO</span>
                      <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md py-1.5 px-2 text-[11px] outline-none text-slate-600 dark:text-slate-300 w-full flex-1" />
                    </div>
                  </div>
                </div>
              </div>
  
              <div className="p-4 border-t border-slate-100 dark:border-slate-700 flex gap-3 flex-shrink-0 bg-slate-50/50 dark:bg-slate-900/20 rounded-b-xl">
                 <button onClick={resetFilters} className="flex-1 py-2.5 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 font-bold rounded-lg text-sm transition-colors hover:bg-slate-100 dark:hover:bg-slate-700">Clear</button>
                 <button onClick={() => setIsFilterModalOpen(false)} className="flex-[2] py-2.5 bg-indigo-600 text-white font-bold rounded-lg text-sm transition-colors hover:bg-indigo-700 shadow-sm">Apply Filters</button>
              </div>
            </div>
          </div>,
          document.body
        )}
  
        {/* 1. COMPACT VIEW DOCUMENTS MODAL */}
        {viewModal && createPortal(
          <div className="fixed top-0 left-0 w-full h-full z-[9999] flex items-center justify-center p-3 sm:p-4 bg-fade-in">
            <div className="bg-white dark:bg-slate-800 rounded-xl w-full max-w-xl shadow-2xl relative flex flex-col max-h-[85vh] sm:max-h-[80vh] modal-blow-up border border-transparent dark:border-slate-700">
              
              <button onClick={closeViewModal} className="absolute top-3 right-3 sm:top-4 sm:right-4 text-gray-400 dark:text-slate-500 hover:text-red-500 dark:hover:text-red-400 transition-colors bg-white dark:bg-slate-700 rounded-full p-1.5 z-10">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"></path></svg>
              </button>
  
              {/* Header */}
              <div className="pt-5 pb-4 px-6 text-center flex-shrink-0 border-b border-gray-100 dark:border-slate-700">
                <h2 className="text-xl font-bold text-gray-800 dark:text-slate-100">KYC Documents</h2>
                <p className="text-xs sm:text-sm font-medium text-slate-500 mt-0.5">Proof submissions for <span className="font-bold text-slate-700 dark:text-slate-300">{viewModal.name || viewModal.id}</span></p>
              </div>
  
              {/* Body - Only the Image Grid Remains */}
              <div className="flex flex-col px-6 py-5 overflow-y-auto custom-scrollbar flex-1">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { key: 'aadhar_front', label: 'Aadhaar Front' },
                    { key: 'aadhar_back', label: 'Aadhaar Back' },
                    { key: 'pan_front', label: 'PAN Front' },
                    { key: 'pan_back', label: 'PAN Back' }
                  ].map((doc) => (
                    <div key={doc.key} className="flex flex-col">
                      <span className="font-bold text-slate-500 dark:text-slate-400 text-xs mb-1.5">{doc.label}</span>
                      <div className="border border-slate-200 dark:border-slate-600 rounded-lg p-1.5 bg-slate-50 dark:bg-slate-700/50 h-32 sm:h-36 flex items-center justify-center relative overflow-hidden group">
                        
                        {docsLoading ? (
                          <div className="flex flex-col items-center">
                            <Spin cls="w-6 h-6 text-indigo-400 mb-2" />
                            <span className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">Fetching</span>
                          </div>
                        ) : docImages[doc.key] ? (
                          <button 
                            onClick={() => setFullscreenImage(docImages[doc.key])}
                            className="w-full h-full flex items-center justify-center focus:outline-none"
                          >
                            <img src={docImages[doc.key]} alt={doc.label} className="w-full h-full object-contain rounded" />
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center cursor-pointer rounded backdrop-blur-[1px]">
                              <span className="text-white text-xs font-bold px-3 py-1.5 bg-black/60 rounded flex items-center gap-1.5">
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"></path></svg>
                                Enlarge
                              </span>
                            </div>
                          </button>
                        ) : (
                          <span className="text-xs font-bold text-slate-400">Not Uploaded</span>
                        )}
  
                      </div>
                    </div>
                  ))}
                </div>
              </div>
  
              {/* Compact Footer just for closing */}
              <div className="py-4 px-6 flex items-center justify-center flex-shrink-0 bg-slate-50/50 dark:bg-slate-900/20 border-t border-gray-100 dark:border-slate-700 rounded-b-xl">
                 <button onClick={closeViewModal} className="w-full sm:w-1/2 py-2.5 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 font-bold rounded-lg text-sm transition-colors hover:bg-slate-100 dark:hover:bg-slate-700">
                   Close Viewer
                 </button>
              </div>
  
            </div>
          </div>,
          document.body
        )}
  
        {/* 2. IN-PAGE FULLSCREEN IMAGE VIEWER (Lightbox) */}
        {fullscreenImage && createPortal(
          <div className="fixed inset-0 z-[100000] bg-black/95 flex items-center justify-center p-4 sm:p-8 backdrop-blur-sm animate-in fade-in duration-200" onClick={() => setFullscreenImage(null)}>
            <button onClick={() => setFullscreenImage(null)} className="absolute top-4 right-4 sm:top-6 sm:right-6 text-white/70 hover:text-white bg-black/50 hover:bg-black/80 rounded-full p-2 transition-all">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
            </button>
            <img src={fullscreenImage} alt="Fullscreen Document" className="max-w-full max-h-full object-contain rounded-lg shadow-2xl animate-in zoom-in-95 duration-300" onClick={(e) => e.stopPropagation()} />
          </div>,
          document.body
        )}
  
        {/* 3. QUICK APPROVE CONFIRMATION MODAL */}
        {approveModal && (
          <ModalWrap maxWidth="max-w-sm">
            <div className="p-6 sm:p-8">
              <div className="w-14 h-14 bg-green-100 dark:bg-green-900/40 rounded-full flex items-center justify-center mx-auto mb-5">
                <svg className="w-7 h-7 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"/></svg>
              </div>
              <h3 className="text-xl font-bold text-center text-slate-900 dark:text-white mb-2">Approve Details?</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 text-center mb-8">
                Confirm approval for <span className="font-bold text-slate-700 dark:text-slate-200">{approveModal.name}</span>.
              </p>
              <div className="flex gap-4">
                <button onClick={() => setApproveModal(null)} disabled={actionLoading}
                  className="flex-1 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 font-bold text-sm hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 transition-colors">
                  Cancel
                </button>
                <button onClick={handleApprove} disabled={actionLoading}
                  className="flex-1 py-2.5 rounded-lg bg-green-600 hover:bg-green-700 text-white font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-50 transition-colors shadow-sm">
                  {actionLoading && <Spin />} Approve
                </button>
              </div>
            </div>
          </ModalWrap>
        )}
  
        {/* 4. QUICK REJECT & REMARK MODAL */}
        {rejectModal && (
          <ModalWrap maxWidth="max-w-md">
            <div className="p-6 sm:p-8">
              <div className="w-14 h-14 bg-red-100 dark:bg-red-900/40 rounded-full flex items-center justify-center mx-auto mb-5">
                <svg className="w-7 h-7 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 18L18 6M6 6l12 12"/></svg>
              </div>
              <h3 className="text-xl font-bold text-center text-slate-900 dark:text-white mb-2">Reject Details</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 text-center mb-6">
                Rejecting details for <span className="font-bold text-slate-700 dark:text-slate-200">{rejectModal.name}</span>. Provide a reason.
              </p>
              <textarea value={rejectRemark} onChange={e => setRejectRemark(e.target.value)}
                placeholder="Provide a reason..."
                className="w-full h-28 p-4 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-sm outline-none focus:border-indigo-500 dark:text-white resize-none mb-6 shadow-inner" />
              <div className="flex gap-4">
                <button onClick={() => { setRejectModal(null); setRejectRemark(''); }} disabled={actionLoading}
                  className="flex-1 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 font-bold text-sm hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 transition-colors">
                  Cancel
                </button>
                <button onClick={handleReject} disabled={actionLoading || !rejectRemark.trim()}
                  className="flex-1 py-2.5 rounded-lg bg-red-600 hover:bg-red-700 text-white font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm">
                  {actionLoading && <Spin />} Reject
                </button>
              </div>
            </div>
          </ModalWrap>
        )}
  
      </div>
    );
};

export default AllKYC;